/**
 * Encrypted document images -> plain data: images, for EXPORT surfaces only.
 *
 * Saved document HTML carries encrypted images as
 * `<img src=<bucket-url> data-enc-key=... data-enc="aes-gcm-256" ...>` (see
 * sanitizeHtml.js sanitizeImg / lexicalNodes/DocImageNode.js). Every export
 * surface — PDF (documentPdf.js), standalone HTML file (documentExport.js),
 * the print overlay (DocumentPrintView.js) — needs the same two things done to
 * that shape before it can emit anything:
 *
 *  1. RESOLVE: fetch the ciphertext and decrypt it into a `data:` URI the
 *     export can embed. The PDF mapper's imageBlock is data:-only BY DESIGN
 *     (its words: "a document export that silently makes network requests to
 *     whatever a pasted <img> points at is a tracking pixel with extra
 *     steps"), and this module keeps that stance: the ONLY urls it will ever
 *     fetch are ours (utils/docMediaHosts.js isAllowedMediaUrl) — anything
 *     else is removed without a request being made.
 *  2. STRIP THE KEY: `data-enc-key` is the per-attachment AES key. Inside the
 *     app the document body is the access boundary, so the key riding in the
 *     HTML is correct there — but an EXPORTED file travels (mail, chat,
 *     shared drives), and a key next to a public ciphertext URL decrypts the
 *     image for anyone holding the file. Exports must NEVER carry keys: a
 *     resolved image is rebuilt with src/alt/width/height and nothing else,
 *     and an image that cannot be resolved is REMOVED, key and all.
 *
 * Failure semantics: any per-image failure (foreign host, fetch, decrypt,
 * oversize) removes THAT image and continues — an export with a gap beats a
 * broken half-export, and beats one that leaks the key. Callers get
 * `{ resolved, removed }` counts alongside the html so they can surface a
 * notice; today they just use the html.
 *
 * Input contract: an ALREADY SANITIZED html string (sanitizeRichTextHtml) —
 * `resolveExportBodyHtml` below packages the sanitize+resolve pair, which is
 * the shape every export call site actually wants.
 */
import { isAllowedMediaUrl } from './docMediaHosts';
import { decryptMediaBuffer } from './docMediaCrypto';
import { sanitizeRichTextHtml, DOC_MEDIA_MIME_RE, DOC_MEDIA_DIM_RE } from './sanitizeHtml';

/** Same ceiling as documentPdf.js MAX_IMAGE_BYTES — one 4 MB posture, applied
 *  here to the DECRYPTED bytes. */
const DEFAULT_MAX_IMAGE_BYTES = 4 * 1024 * 1024;

/** Decrypted Blob -> its base64 payload. FileReader rather than
 *  `blob.arrayBuffer()`: jsdom (the test realm) has no Blob.arrayBuffer, and
 *  readAsDataURL is the codepath the editor already uses for pasted images
 *  (RichTextEditor.js). The mime prefix of the reader's result is DISCARDED —
 *  the data: URI is rebuilt from the validated data-mime attribute instead,
 *  so a blob with a stray type cannot smuggle one into the export. */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || '');
      const comma = s.indexOf(',');
      if (comma < 0) reject(new Error('unreadable decrypted image'));
      else resolve(s.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('unreadable decrypted image'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Resolve every encrypted image in a sanitized html string.
 *
 * @param {string} html  already-sanitized document html
 * @param {object} [opts]
 * @param {number}   [opts.maxBytes]    decrypted-size ceiling (default 4 MB)
 * @param {Function} [opts.fetchImpl]   `(url) => Promise<Response>` — the
 *   webview's own fetch by default (NEVER the Tauri Rust adapter, which
 *   corrupts binary bodies — see utils/decryptedMediaCache.js)
 * @param {Function} [opts.decryptImpl] `(keyB64, arrayBuffer, mime) => Promise<Blob>`
 * @returns {Promise<{ html: string, resolved: number, removed: number }>}
 */
export async function resolveEncryptedImagesInHtml(
  html,
  { maxBytes = DEFAULT_MAX_IMAGE_BYTES, fetchImpl, decryptImpl } = {}
) {
  const input = typeof html === 'string' ? html : '';
  // Fast path: a document with no encrypted/pending images (the common case)
  // is returned BYTE-IDENTICAL — no DOM round trip to reformat it, and no
  // chance of touching fetch at all.
  if (!/data-enc-key|data-pending-ref/.test(input)) {
    return { html: input, resolved: 0, removed: 0 };
  }

  const doFetch = fetchImpl || ((url) => fetch(url));
  const doDecrypt = decryptImpl || decryptMediaBuffer;

  const doc = new DOMParser().parseFromString(input, 'text/html');
  const imgs = [...doc.body.querySelectorAll('img[data-enc-key], img[data-pending-ref]')];
  let resolved = 0;
  let removed = 0;

  for (const img of imgs) {
    const src = img.getAttribute('src') || '';
    const keyB64 = img.getAttribute('data-enc-key') || '';
    const pendingRef = img.getAttribute('data-pending-ref') || '';

    // Pending images were never uploaded — there is no ciphertext to fetch,
    // so there is nothing an export could show. (A key without a src is the
    // same non-image.)
    if (pendingRef || !src || !keyB64) {
      img.remove();
      removed += 1;
      continue;
    }
    // The anti-tracking stance, restated from imageBlock's original rationale:
    // an export that fetches whatever a pasted <img> points at is a tracking
    // pixel with extra steps. Only OUR hosts are ever requested; a foreign
    // src is removed with NO network request made.
    if (!isAllowedMediaUrl(src)) {
      img.remove();
      removed += 1;
      continue;
    }

    try {
      const res = await doFetch(src);
      if (!res || !res.ok) throw new Error(`media fetch failed (${res && res.status})`);
      const buf = await res.arrayBuffer();
      const mimeAttr = img.getAttribute('data-mime') || '';
      const mime = DOC_MEDIA_MIME_RE.test(mimeAttr) ? mimeAttr : 'image/png';
      const blob = await doDecrypt(keyB64, buf, mime);
      if (!blob || typeof blob.size !== 'number') throw new Error('decrypt produced no image');
      // documentPdf's 4 MB posture, on the decrypted bytes: an image this
      // large would freeze pdfmake's main-thread layout and bloat every file.
      if (blob.size > maxBytes) {
        img.remove();
        removed += 1;
        continue;
      }
      const b64 = await blobToBase64(blob);

      // REBUILD the attribute set from scratch (the sanitizer's own
      // wipe-then-rebuild posture): src + alt + width/height and NOTHING
      // else. This is the key-leak fix — every data-enc-*/data-pending-*
      // attribute (and the machinery metadata) must be absent from exports.
      const alt = img.getAttribute('alt') || '';
      const w = img.getAttribute('data-w') || '';
      const h = img.getAttribute('data-h') || '';
      for (const attr of [...img.attributes]) img.removeAttribute(attr.name);
      img.setAttribute('src', `data:${mime};base64,${b64}`);
      if (alt) img.setAttribute('alt', alt);
      // Both-or-neither, like the sanitizer: a lone dimension distorts.
      if (DOC_MEDIA_DIM_RE.test(w) && DOC_MEDIA_DIM_RE.test(h)) {
        img.setAttribute('width', w);
        img.setAttribute('height', h);
      }
      resolved += 1;
    } catch {
      // Fetch/decrypt/re-encode failure: this image is removed (key included)
      // and the rest still resolve — never a broken half-export.
      img.remove();
      removed += 1;
    }
  }

  return { html: doc.body.innerHTML, resolved, removed };
}

/**
 * The shape every export call site wants: sanitize, then resolve, and hand
 * back just the html. One await at each caller (documentPdf.js,
 * documentFolderPdf.js, documentExport.js, DocumentPrintView.js) — the
 * sanitize-first contract lives HERE so no surface can forget it.
 */
export async function resolveExportBodyHtml(bodyHtml, opts) {
  const { html } = await resolveEncryptedImagesInHtml(sanitizeRichTextHtml(bodyHtml || ''), opts);
  return html;
}
